/**
 * Which character are you — the quiz.
 *
 * Twelve biblical figures, ten questions, and a weighted score. It exists to be
 * shared, which sets most of the design: it is free, it needs no account, it
 * takes about ninety seconds, and it ends on something worth screenshotting.
 *
 * ── How the scoring works ───────────────────────────────────────────────────
 *
 * Every option carries points for two or three characters rather than one. A
 * quiz where each answer maps to exactly one figure is a quiz people can see
 * through on question three, and seeing through it is the thing that stops it
 * being shared.
 *
 * The weights are set by what the option actually says, not by its letter. "I
 * ask for a sign first" is Gideon and his fleece before it is anything else;
 * "loving the wrong person too deeply" is David and Bathsheba, not a generic
 * doubter. A letter-based scheme would have put both in the wrong place.
 *
 * Questions 4, 8 and 10 carry `weight: 1.5`. They ask where someone is now —
 * their relationship with God, the season they are in, one word for their faith
 * — and a result that contradicts those reads as wrong however well the other
 * seven fit. They also break ties, for the same reason.
 *
 * ── On the verses ───────────────────────────────────────────────────────────
 *
 * All twelve are the World English Bible, public domain, matching the rest of
 * the app. The wording supplied with the brief was mostly New International
 * Version, which is Biblica's copyright and cannot ship on a site that charges
 * money. Where the app's own verse deck already holds the reference, the text
 * here is copied from it exactly so the same verse never appears two ways.
 */

/**
 * The characters, in no particular order — the order they are declared has no
 * effect on scoring, only on the tie-break of last resort.
 */
export const CHARACTERS = {
  jonah: {
    id: 'jonah',
    name: 'Jonah',
    emoji: '🐋',
    tagline: 'The reluctant prophet',
    body: 'You are most like Jonah — someone who knows exactly what they are called to do and is actively moving in the opposite direction. It is not rebellion. It is fear dressed up as logic. The storm in your life is not punishment, it is redirection. God does not call people who feel ready. He calls people who are needed.',
    reveals:
      'You already know what you are running from. The question is not whether God will call again — it is how long you will make him wait.',
    verse: { text: 'Salvation belongs to the LORD.', ref: 'Jonah 2:9' },
  },

  david: {
    id: 'david',
    name: 'David',
    emoji: '👑',
    tagline: 'The gifted and deeply flawed king',
    body: 'You are most like David — anointed, talented, genuinely after God’s heart, and also someone who has made serious mistakes that still follow them. David did things that should have disqualified him. They did not. What made him extraordinary was never being faultless; it was his pattern of turning back. You have not gone too far.',
    reveals:
      'Your failures do not cancel the calling on your life. The same hands that did the damage wrote the most honest prayers anyone has ever written down.',
    verse: {
      text: 'Create in me a clean heart, O God. Renew a right spirit within me.',
      ref: 'Psalm 51:10',
    },
  },

  prodigal: {
    id: 'prodigal',
    name: 'The Prodigal Son',
    emoji: '🏠',
    tagline: 'The wanderer who is still welcome',
    body: 'You are most like the Prodigal Son — someone who has drifted from who they know they are meant to be. Maybe from faith, maybe from family, maybe from themselves. The father in that story did not wait at the door with his arms folded. He saw his son a long way off and ran. You do not have to clean yourself up first.',
    reveals:
      'The distance between you and home is shorter than it feels from where you are standing. The door has not closed.',
    verse: {
      text: 'While he was still far off, his father saw him, and was moved with compassion, and ran.',
      ref: 'Luke 15:20',
    },
  },

  joseph: {
    id: 'joseph',
    name: 'Joseph',
    emoji: '🌟',
    tagline: 'The dreamer in the pit',
    body: 'You are most like Joseph — someone with real vision and a real calling, currently in a season that looks nothing like the promise. Betrayal, waiting, being forgotten by people who said they would help. Joseph went from a pit to a prison before he saw a palace. What was done to you is being worked into something you cannot see yet.',
    reveals:
      'The dream has not expired. What looks like your lowest point is closer to the setup than the ending.',
    verse: {
      text: 'As for you, you meant evil against me, but God meant it for good, to save many people alive.',
      ref: 'Genesis 50:20',
    },
  },

  peter: {
    id: 'peter',
    name: 'Peter',
    emoji: '🔥',
    tagline: 'The passionate and impulsive one',
    body: 'You are most like Peter — someone who loves fiercely, moves fast, and sometimes speaks before the thought has finished. You have probably said yes without counting the cost, or gone quiet about something important when the pressure came. Peter denied Jesus three times and still became the rock the church was built on. Your intensity is not a disqualification.',
    reveals:
      'The same fire that gets you into trouble is the thing that is wanted. It needs aiming, not putting out.',
    verse: {
      text: 'On this rock I will build my assembly, and the gates of Hades will not prevail against it.',
      ref: 'Matthew 16:18',
    },
  },

  esther: {
    id: 'esther',
    name: 'Esther',
    emoji: '👸',
    tagline: 'The unlikely one, in the right place',
    body: 'You are most like Esther — put exactly where you are for a reason bigger than your comfort. You feel unqualified for what is in front of you, and stepping forward looks like it costs too much. Esther was terrified. She fasted for three days before walking into a throne room uninvited, knowing it could kill her. She was born for it anyway.',
    reveals:
      'Your position is not an accident. The question Mordecai put to Esther is the one now being put to you.',
    verse: {
      text: 'Who knows if you haven’t come to the kingdom for such a time as this?',
      ref: 'Esther 4:14',
    },
  },

  moses: {
    id: 'moses',
    name: 'Moses',
    emoji: '🌊',
    tagline: 'The leader who doubted his own voice',
    body: 'You are most like Moses — a real calling that feels impossibly large, and an honest conviction that you are the wrong person for it. At the burning bush Moses listed every reason. Too slow of speech. Nobody will listen. Send somebody else. God sent him, and parted a sea through him. Feeling inadequate is not disqualifying. It is closer to the point.',
    reveals:
      'God has never once called somebody who was already equipped. Your weakness is where the strength shows.',
    verse: {
      text: 'I will be with your mouth, and teach you what you shall speak.',
      ref: 'Exodus 4:12',
    },
  },

  ruth: {
    id: 'ruth',
    name: 'Ruth',
    emoji: '🌾',
    tagline: 'The faithful one in the quiet season',
    body: 'You are most like Ruth — loyal and steady in a season nobody is watching or applauding. You did not choose the circumstances you are in, and you are showing up in them anyway. Ruth gathered leftover grain in fields that were not hers, in a country that was not hers, inside somebody else’s story. That is what put her where redemption found her.',
    reveals:
      'Faithfulness nobody sees is never wasted. What you are doing quietly is building something you cannot see the shape of yet.',
    verse: {
      text: 'Where you go, I will go; and where you stay, I will stay.',
      ref: 'Ruth 1:16',
    },
  },

  gideon: {
    id: 'gideon',
    name: 'Gideon',
    emoji: '⚔️',
    tagline: 'The warrior who was hiding',
    body: 'You are most like Gideon — called mighty while hiding, convinced you are the least qualified person in the room. When the angel found him he was threshing wheat in a winepress, out of sight of the enemy. He was called a mighty warrior in that exact moment. Not for what he was. For what he was turning into.',
    reveals:
      'You are being seen as who you are becoming. The call came to you because you doubted yourself, not in spite of it.',
    verse: {
      text: 'The LORD is with you, you mighty man of valour!',
      ref: 'Judges 6:12',
    },
  },

  elijah: {
    id: 'elijah',
    name: 'Elijah',
    emoji: '⚡',
    tagline: 'The exhausted one under the tree',
    body: 'You are most like Elijah — someone who has done real things in faith and is now completely spent, sitting under a tree wanting it to stop. He called down fire from heaven and ran for his life the next day. Being burnt out does not mean your faith has broken. It means you are a person. God did not tell Elijah off under that tree. He let him sleep, and sent food.',
    reveals:
      'Exhaustion is not failure. God met Elijah at his lowest with gentleness rather than judgement, and that is the pattern.',
    verse: {
      text: 'Arise and eat, because the journey is too great for you.',
      ref: '1 Kings 19:7',
    },
    // Elijah is the one result that lands on somebody who may genuinely be
    // struggling. Quiet, once, at the foot of the card — not a warning banner.
    support:
      'If things are darker than tired, please talk to someone. In the UK, Samaritans are on 116 123, free, any hour.',
  },

  thomas: {
    id: 'thomas',
    name: 'Thomas',
    emoji: '🕊️',
    tagline: 'The honest doubter',
    body: 'You are most like Thomas — someone whose faith is real but who needs evidence, honesty, and room to ask. He gets called Doubting Thomas as though it were an insult. Jesus did not tell him off. He turned up specifically for Thomas and offered him his hands. Honest doubt that moves toward the truth is not the opposite of faith.',
    reveals:
      'Your questions are not disqualifying. Bring them as they are. Jesus has a habit of showing up for the people who need to see.',
    verse: { text: 'Don’t be unbelieving, but believing.', ref: 'John 20:27' },
  },

  daniel: {
    id: 'daniel',
    name: 'Daniel',
    emoji: '🦁',
    tagline: 'The one who would not bow',
    body: 'You are most like Daniel — deep conviction, inside a culture pulling hard the other way. Daniel did not withdraw from Babylon. He served it well, and still would not bend on the things that mattered. He was thrown to lions for praying with his window open. Your consistency where it costs something is the most persuasive thing about you.',
    reveals:
      'You do not have to leave the world to stay faithful in it. Being where you are is not a compromise; it is the assignment.',
    verse: {
      text: 'My God has sent his angel, and has shut the lions’ mouths.',
      ref: 'Daniel 6:22',
    },
  },
};

export const CHARACTER_LIST = Object.values(CHARACTERS);

/**
 * The ten questions.
 *
 * `weight` multiplies every point an option awards. Only questions 4, 8 and 10
 * carry one, and they are also the tie-break order — see lib/characterQuiz.js.
 *
 * The brief gave question 1 four options and the other nine five. The fifth
 * here — freezing rather than fleeing or fighting — is the one the set was
 * missing, and without it the first thing a player meets is a grid that does
 * not match the nine after it.
 */
export const QUESTIONS = [
  {
    id: 'q1',
    text: 'When things go wrong, your first reaction is:',
    options: [
      { text: 'Run away and deal with it later', scores: { jonah: 3, prodigal: 2 } },
      { text: 'Fight back immediately', scores: { peter: 3, david: 2 } },
      { text: 'Overthink it and spiral into doubt', scores: { thomas: 3, elijah: 3 } },
      { text: 'Pray, and trust the process', scores: { daniel: 3, ruth: 2 } },
      { text: 'Go quiet and hope it passes', scores: { gideon: 3, moses: 2, esther: 1 } },
    ],
  },
  {
    id: 'q2',
    text: 'Your biggest personal weakness is:',
    options: [
      { text: 'Avoiding what I know I should do', scores: { jonah: 3, moses: 3 } },
      { text: 'Pride, and wanting the recognition', scores: { david: 3, peter: 2 } },
      { text: 'Loving the wrong person or thing too much', scores: { david: 3, prodigal: 2 } },
      { text: 'Impatience — I want it now', scores: { peter: 3, prodigal: 2 } },
      { text: 'Fear of being seen, or of speaking up', scores: { gideon: 3, moses: 3, esther: 2 } },
    ],
  },
  {
    id: 'q3',
    text: 'People who know you would call you:',
    options: [
      { text: 'Reliable, but complicated', scores: { joseph: 2, david: 2, jonah: 1 } },
      { text: 'A natural leader', scores: { david: 3, peter: 2 } },
      { text: 'Passionate — sometimes too much', scores: { peter: 3, elijah: 3 } },
      { text: 'Quietly, consistently faithful', scores: { ruth: 3, daniel: 2 } },
      { text: 'Wise beyond your years', scores: { daniel: 3, joseph: 2, esther: 1 } },
    ],
  },
  {
    id: 'q4',
    weight: 1.5,
    text: 'Honestly, your relationship with God right now is:',
    options: [
      { text: 'Running from him, if I’m being real', scores: { jonah: 4, prodigal: 3 } },
      { text: 'Complicated — I’ve made serious mistakes', scores: { david: 4, peter: 3 } },
      { text: 'Distant. Life got in the way', scores: { prodigal: 3, elijah: 3, jonah: 2 } },
      { text: 'Growing stronger every day', scores: { ruth: 3, daniel: 3, esther: 1 } },
      { text: 'Searching — I want to believe more than I do', scores: { thomas: 4, gideon: 2 } },
    ],
  },
  {
    id: 'q5',
    text: 'Your biggest struggle right now:',
    options: [
      { text: 'Finding my purpose and calling', scores: { moses: 5, gideon: 2 } },
      { text: 'Controlling my ambition and ego', scores: { david: 3, peter: 2 } },
      { text: 'Letting go of someone, or something', scores: { elijah: 3, prodigal: 3 } },
      { text: 'Trusting the plan when I can’t see it', scores: { joseph: 3, ruth: 2 } },
      { text: 'Feeling overlooked and underestimated', scores: { joseph: 3, esther: 2, gideon: 1 } },
    ],
  },
  {
    id: 'q6',
    text: 'When God asks you to do something hard:',
    options: [
      { text: 'I make excuses and find reasons to delay', scores: { jonah: 3, moses: 3 } },
      { text: 'I rush in without thinking it through', scores: { peter: 4, david: 1 } },
      { text: 'I doubt myself and wonder if I heard right', scores: { gideon: 3, thomas: 2 } },
      { text: 'I say yes even when I’m frightened', scores: { esther: 4, ruth: 2 } },
      { text: 'I ask for a sign first', scores: { gideon: 4, thomas: 2 } },
    ],
  },
  {
    id: 'q7',
    text: 'Your loyalty to the people you love is:',
    options: [
      { text: 'Selective — I’m careful who gets it', scores: { daniel: 3, joseph: 2 } },
      { text: 'Fierce and protective, maybe too much', scores: { peter: 3, elijah: 3 } },
      { text: 'Deep, but it has been tested and broken', scores: { joseph: 3, david: 2 } },
      { text: 'Steady, whatever it costs', scores: { ruth: 3, daniel: 2 } },
      { text: 'Hard to hold — I tend to wander', scores: { prodigal: 3, jonah: 2 } },
    ],
  },
  {
    id: 'q8',
    weight: 1.5,
    text: 'This season of your life feels like:',
    options: [
      { text: 'Rock bottom — nowhere to go but up', scores: { elijah: 6, prodigal: 2, joseph: 1 } },
      { text: 'The wilderness. Lost, and not sure where', scores: { moses: 5, elijah: 2, jonah: 1 } },
      { text: 'A turning point — something has to change', scores: { prodigal: 3, peter: 2, jonah: 1 } },
      { text: 'Quiet preparation', scores: { ruth: 3, daniel: 3, joseph: 1 } },
      { text: 'Being overlooked when I have more to give', scores: { joseph: 4, esther: 3, gideon: 1 } },
    ],
  },
  {
    id: 'q9',
    text: 'What you want most right now:',
    options: [
      { text: 'A completely fresh start', scores: { prodigal: 3, jonah: 2 } },
      { text: 'Recognition for what I’ve built', scores: { david: 3, joseph: 2 } },
      { text: 'Real connection, and to be loved', scores: { ruth: 3, prodigal: 2 } },
      { text: 'Clarity on what I do next', scores: { moses: 4, gideon: 2, thomas: 1 } },
      { text: 'To find where I actually belong', scores: { ruth: 3, esther: 3, joseph: 1 } },
    ],
  },
  {
    id: 'q10',
    weight: 1.5,
    text: 'One word for your faith right now:',
    options: [
      { text: 'Reluctant', scores: { jonah: 5, moses: 3, gideon: 2 } },
      { text: 'Complicated', scores: { david: 4, peter: 2, elijah: 2, prodigal: 1 } },
      { text: 'Searching', scores: { thomas: 5, gideon: 2, moses: 1 } },
      { text: 'Growing', scores: { ruth: 3, daniel: 2, esther: 2, peter: 1 } },
      { text: 'Loyal', scores: { daniel: 4, ruth: 3, joseph: 2, esther: 1 } },
    ],
  },
];

/** Ties are broken on these, in order — the questions about where you are now. */
export const TIEBREAK_QUESTION_IDS = ['q4', 'q8', 'q10'];
